import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid } from "@mui/material";
import { makeStyles } from "@mui/styles";
import PropTypes from "prop-types";
import SlideUpTransition from "../../components/fundamentals/SlideUpTransition";
import { EMPLOYEE_BID_DATE_TYPE, EMPLOYEE_BID_STATUS } from "../../util/bid/bidConstants.js";
import { sortOnCaseSensitiveTexts } from "../../util/commonUtils.js";
import { THREE_YEARS_IN_PAST } from "../../util/dateHelper.js";
import { convert_Luxon_To_DateFns } from "../../util/dateTimeFormatter.js";
import {
  BID_TYPE_FILTER_MUST_BE_SELECTED,
  DATE_TYPE_FILTER_MUST_BE_SELECTED,
  STATUS_FILTER_MUST_BE_SELECTED,
} from "../../util/messageConstants.js";
import StarsDatePicker from "../fundamentals/StarsDatePicker.jsx";
import StarsSelectInput from "../fundamentals/StarsSelectInput.jsx";

const useStyles = makeStyles((theme) => ({
  filterInputs: {
    width: "100%",
    textAlign: "left",
  },
}));

const MyBidsFilterDialog = (props) => {
  const classes = useStyles();

  const renderDateTypeSelectList = () => {
    return (
      <StarsSelectInput
        label="Date Type"
        menuMessage="Filter on Date Type"
        value={props.dateTypeFilter}
        allOptions={Object.values(EMPLOYEE_BID_DATE_TYPE)}
        sortComparator={sortOnCaseSensitiveTexts}
        onChange={props.onDateTypeFilterChange.bind(this)}
        errors={props.dateTypeFilter === "" ? DATE_TYPE_FILTER_MUST_BE_SELECTED : ""}
        className={classes.filterInputs}
        fullWidth={true}
      />
    );
  };

  const renderBidTypeSelectList = () => {
    return (
      <StarsSelectInput
        label="Bid Type"
        id="bidTypeSelectElement"
        menuMessage="Filter on Bid Type"
        value={props.bidTypeFilters}
        allOptions={Object.values(props.allBidTypesFilters)}
        sortComparator={sortOnCaseSensitiveTexts}
        errors={props.bidTypeFilters?.length <= 0 ? BID_TYPE_FILTER_MUST_BE_SELECTED : ""}
        className={classes.inputSelect}
        onChange={props.onBidTypeFilterChange.bind(this)}
        multiple={true}
        fullWidth={true}
      />
    );
  };

  const renderStartDatePicker = () => {
    return (
      <StarsDatePicker
        label="Start Date"
        date={props.startDateFilter}
        onChange={props.onStartDateFilterChange}
        dateErrors={props.startDateFilterErrors}
        minDate={convert_Luxon_To_DateFns(THREE_YEARS_IN_PAST(props.stationFilter))}
        className={classes.filterInputs}
        fullWidth={true}
      />
    );
  };

  const renderEndDatePicker = () => {
    return (
      <StarsDatePicker
        label="End Date"
        date={props.endDateFilter}
        onChange={props.onEndDateFilterChange}
        dateErrors={props.endDateFilterErrors}
        minDate={convert_Luxon_To_DateFns(THREE_YEARS_IN_PAST(props.stationFilter))}
        className={classes.filterInputs}
        fullWidth={true}
      />
    );
  };

  const renderBidStatusSelectList = () => {
    return (
      <StarsSelectInput
        label="Status"
        id="bidStatusSelectElement"
        menuMessage="Filter on Status"
        value={props.bidStatusFilters}
        allOptions={Object.values(EMPLOYEE_BID_STATUS)}
        sortComparator={sortOnCaseSensitiveTexts}
        errors={props.bidStatusFilters?.length <= 0 ? STATUS_FILTER_MUST_BE_SELECTED : ""}
        className={classes.inputSelect}
        onChange={props.onStatusFilterChange.bind(this)}
        fullWidth={true}
        multiple={true}
      />
    );
  };

  return (
    <Dialog
      fullScreen
      className="detail-dialog"
      fullWidth
      maxWidth="md"
      TransitionComponent={SlideUpTransition}
      open={props.isMobileFilterDialogOpen}
      onClose={props.onMobileFilterDialogClose.bind(this)}
      aria-labelledby="alert-dialog-slide-title"
      aria-describedby="alert-dialog-slide-description"
    >
      <DialogTitle sx={{ fontWeight: "bold", marginLeft: "15px" }} id="alert-dialog-slide-title">
        Filters
      </DialogTitle>
      <DialogContent sx={{ pb: 0 }}>
        <DialogContentText component={"div"} id="alert-dialog-slide-description">
          <div style={{ margin: "15px" }}>
            <Box>{renderBidTypeSelectList()}</Box>
          </div>
          <div style={{ margin: "15px" }}>
            <Box>{renderBidStatusSelectList()}</Box>
          </div>
          <div style={{ margin: "15px" }}>
            <Box>{renderDateTypeSelectList()}</Box>
          </div>
          <div style={{ margin: "15px" }}>
            <Box>{renderStartDatePicker()}</Box>
          </div>
          <div style={{ margin: "15px" }}>
            <Box>{renderEndDatePicker()}</Box>
          </div>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Grid container justifyContent="flex-end" alignItems={"center"} spacing={1}>
          <Grid item>
            <Button
              sx={{ margin: "5px", width: "100px" }}
              variant="outlined"
              color="primary"
              onClick={props.onMobileFilterDialogClose.bind(this)}
            >
              CANCEL
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              onClick={props.onClickMobileFilterDialogApplyFiltersButton.bind(this)}
              disabled={
                props.startDateFilterErrors !== "" ||
                props.endDateFilterErrors !== "" ||
                props.bidTypeFilters?.length <= 0 ||
                props.bidStatusFilters?.length <= 0
              }
            >
              Apply Filters
            </Button>
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  );
};

MyBidsFilterDialog.propTypes = {
  stationFilter: PropTypes.string.isRequired,
  onClickMobileFilterDialogApplyFiltersButton: PropTypes.func.isRequired,
  startDateFilterErrors: PropTypes.string.isRequired,
  endDateFilterErrors: PropTypes.string.isRequired,
  onStartDateFilterChange: PropTypes.func.isRequired,
  onEndDateFilterChange: PropTypes.func.isRequired,
  startDateFilter: PropTypes.object.isRequired,
  endDateFilter: PropTypes.object.isRequired,
  bidTypeFilters: PropTypes.array.isRequired,
  allBidTypesFilters: PropTypes.array.isRequired,
  bidStatusFilters: PropTypes.array.isRequired,
  dateTypeFilter: PropTypes.string.isRequired,
  onDateTypeFilterChange: PropTypes.func.isRequired,
  onBidTypeFilterChange: PropTypes.func.isRequired,
  onStatusFilterChange: PropTypes.func.isRequired,
  onMobileFilterDialogClose: PropTypes.func.isRequired,
  isMobileFilterDialogOpen: PropTypes.bool,
};

export default MyBidsFilterDialog;
